/**
 * RevenueCat's own headline totals, as last fetched.
 *
 * Written by `scripts/syncRevenueCatOverview.ts` and read here. It exists for
 * one job: letting a figure computed from our mirror be checked against the
 * authority it is a mirror *of*.
 *
 * On 26 Aug 2026 the dashboard said 25 active trials and RevenueCat said 19 —
 * the mirror had not been refreshed for seventeen days and eighteen documents
 * still marked `trialing` had already expired. Every local query was correct.
 * Nothing on screen could have told anybody, because a count over stale data
 * looks exactly like a count over fresh data. This is the second opinion.
 */
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'

export interface RevenueCatOverview {
  activeTrials: number | null
  activeSubscriptions: number | null
  activeUsers: number | null
  mrr: number | null
  revenue: number | null
  newCustomers: number | null
  /** ISO timestamp of the fetch — how old this second opinion is. */
  fetchedAt: string
  source: string
}

/** How much of the mirror still describes what RevenueCat said at the last pass. */
export interface MirrorFreshness {
  /**
   * Production subscriptions that claim to be live — the population every
   * figure on the row is computed over. Sandbox rows are not counted.
   */
  total: number
  /** Of those, how many have not been re-confirmed inside the window. */
  stale: number
  /** The window `stale` is measured over, in days. */
  withinDays: number
  /** The single newest write — what this check used to read on its own. */
  newestAt: string | null
}

/**
 * How stale the subscription mirror is — counted across it, not sampled from it.
 *
 * ## What this replaced, and why it had to be replaced
 *
 * This function used to be `orderBy('updatedAt', 'desc').limit(1)` — the age of
 * the *newest* document — on the reasoning that a mirror nobody had written to
 * for seventeen days was measurable directly rather than inferred from a delta.
 * That reasoning was right. The measurement was not.
 *
 * A webhook writes one document per event. So the newest write is the last time
 * *anything* changed anywhere, and on 3 Sep 2026 it read 0.1 days old while
 * **5,219 of 5,291 documents had not been touched since the 26 Aug backfill**.
 * One RENEWAL at 12:06 was enough to certify the whole collection fresh. The
 * check written to catch the 26 Aug incident could not see the 26 Aug incident,
 * and by construction never could: it cannot fail while the webhook delivers a
 * single event a week.
 *
 * ## Why counting old documents is the right measure here
 *
 * It would be the wrong measure for a webhook alone. Most subscriptions do not
 * change for months — a yearly plan produces one event a year — so "how many
 * documents are old" would be a permanent false alarm against a healthy feed.
 *
 * It is the right measure against a **full pass**, because a full pass writes
 * every document it covers. `crmRevenueCat` re-asserts them nightly from
 * RevenueCat's own API, so after a pass this count is near zero. A large count
 * means no pass has run, which is exactly the condition that leaves state wrong:
 * a document only corrects itself if RevenueCat happened to send an event for
 * that subscription, and a trial that quietly lapsed during a delivery outage
 * never does.
 *
 * ## Only the subscriptions that claim to be live
 *
 * Which is the same population `crmRevenueCat` re-asserts, and for the same
 * reason: a subscription that expired in March cannot change, so its document
 * being five months old is correct rather than stale. Counting those made this
 * read 49% immediately after a clean pass — a banner that fires every day is a
 * banner nobody reads by the end of the week, which is the failure the first
 * version of this warning was written to avoid.
 *
 * A live subscription being old is the actual risk: it is the one that may have
 * lapsed, cancelled or converted without us hearing, and every figure in TRY and
 * BUY is computed over exactly this set.
 *
 * Two count aggregations, both on `sandbox, expiresAtMs, updatedAt`. Nothing but
 * the numbers crosses the wire.
 */
export async function loadMirrorFreshness(withinDays = 2): Promise<MirrorFreshness> {
  const now = Date.now()
  const base = query(
    collection(db, CRM_COLLECTIONS.subscriptions),
    where('sandbox', '==', false),
    where('expiresAtMs', '>', now),
  )
  const cutoff = Timestamp.fromMillis(now - withinDays * 86_400_000)
  const [total, stale, newest] = await Promise.all([
    getCountFromServer(base),
    getCountFromServer(query(base, where('updatedAt', '<', cutoff))),
    getDocs(query(base, orderBy('updatedAt', 'desc'), limit(1))),
  ])
  const v = newest.docs[0]?.get('updatedAt') as { toDate?: () => Date } | string | undefined
  return {
    total: total.data().count,
    stale: stale.data().count,
    withinDays,
    newestAt:
      typeof v === 'string'
        ? v
        : v && typeof v.toDate === 'function'
          ? v.toDate().toISOString()
          : null,
  }
}

/** Null when the collector has never run, which is a missing check, not a pass. */
export async function loadRevenueCatOverview(): Promise<RevenueCatOverview | null> {
  const snap = await getDoc(doc(db, 'skidaCrmSettings', 'revenueCatOverview'))
  if (!snap.exists()) return null
  const v = snap.data()
  const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : null)
  return {
    activeTrials: num(v.activeTrials),
    activeSubscriptions: num(v.activeSubscriptions),
    activeUsers: num(v.activeUsers),
    mrr: num(v.mrr),
    revenue: num(v.revenue),
    newCustomers: num(v.newCustomers),
    fetchedAt: typeof v.fetchedAt === 'string' ? v.fetchedAt : '',
    source: typeof v.source === 'string' ? v.source : 'RevenueCat',
  }
}
